import "./index.css";
import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import { faCrown } from "@fortawesome/free-solid-svg-icons";

// Placeholder data
const liverpoolFinishesInPremierLeague = [
    { season: "2024/25", position: 1 },
    { season: "2023/24", position: 3 },
    { season: "2022/23", position: 5 },
    { season: "2021/22", position: 2 },
    { season: "2020/21", position: 3 },
    { season: "2019/20", position: 1 },
    { season: "2018/19", position: 2 },
    { season: "2017/18", position: 4 },
    { season: "2016/17", position: 4 },
    { season: "2015/16", position: 8 },
    { season: "2014/15", position: 6 },
    { season: "2013/14", position: 2 },
    { season: "2012/13", position: 7 },
    { season: "2011/12", position: 8 },
    { season: "2010/11", position: 6 },
    { season: "2009/10", position: 7 },
    { season: "2008/09", position: 2 },
    { season: "2007/08", position: 4 },
    { season: "2006/07", position: 3 },
    { season: "2005/06", position: 3 },
    { season: "2004/05", position: 5 },
    { season: "2003/04", position: 4 },
    { season: "2002/03", position: 5 },
    { season: "2001/02", position: 2 },
    { season: "2000/01", position: 3 },
    { season: "1999/00", position: 4 },
    { season: "1998/99", position: 7 },
    { season: "1997/98", position: 3 },
    { season: "1996/97", position: 4 },
    { season: "1995/96", position: 3 },
    { season: "1994/95", position: 4 },
    { season: "1993/94", position: 8 },
    { season: "1992/93", position: 6 },
];

const SimpleVisualizations = () => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    const parsedAndSortedData = [...liverpoolFinishesInPremierLeague]
      .map(d => ({ ...d, startYear: parseInt(d.season.split("/")[0], 10) }))
      .sort((a, b) => a.startYear - b.startYear);

    const container = d3.select(chartRef.current);
    container.selectAll("*").remove();

    const width = 640;
    const height = 360;
    const margin = { top: 20, right: 20, bottom: 60, left: 48 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const svg = container
      .append("svg")
      .attr("width", "100%")
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("preserveAspectRatio", "xMidYMid meet");

    const g = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const x = d3
      .scalePoint()
      .domain(parsedAndSortedData.map(d => d.season))
      .range([0, innerWidth])
      .padding(0.5);

    const maxPosition = d3.max(parsedAndSortedData, d => d.position) || 1;
    const y = d3
      .scaleLinear()
      // Invert so position 1 is at the top
      .domain([maxPosition + 1, 1])
      .range([innerHeight, 0]);

    const line = d3
      .line()
      .x(d => x(d.season))
      .y(d => y(d.position))
      .curve(d3.curveMonotoneX);

    const xAxis = d3
      .axisBottom(x)
      .tickSize(0)
      .tickPadding(8)
      .tickFormat(d => {
        if (typeof d !== "string") return d;
        const parts = d.split("/");
        if (parts.length !== 2) return d;
        const start = parts[0];
        const end = parts[1];
        return `${start.slice(-2)}/${end}`;
      });
    const yAxis = d3.axisLeft(y).ticks(Math.min(maxPosition, 10)).tickFormat(d3.format("d"));

    g.append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(xAxis)
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end");

    g.append("g").call(yAxis);

    g.append("path")
      .datum(parsedAndSortedData)
      .attr("fill", "none")
      .attr("stroke", "#e31a1c")
      .attr("stroke-width", 2)
      .attr("d", line);

    g.selectAll(".point")
      .data(parsedAndSortedData)
      .enter()
      .append("circle")
      .attr("class", "point")
      .attr("cx", d => x(d.season))
      .attr("cy", d => y(d.position))
      .attr("r", 3)
      .attr("fill", "#e31a1c");

    // Add a gold Font Awesome crown for champions (position === 1)
    const crownData = parsedAndSortedData.filter(d => d.position === 1);
    const iconWidth = faCrown.icon[0];
    const iconHeight = faCrown.icon[1];
    const crownPath = faCrown.icon[4];

    g.selectAll(".fa-crown")
      .data(crownData)
      .enter()
      .append("path")
      .attr("class", "fa-crown")
      .attr("d", crownPath)
      .attr("fill", "#d4af37")
      .attr("stroke", "#b8860b")
      .attr("stroke-width", 0.75)
      .attr("transform", d => {
        const xPos = x(d.season) || 0;
        const yPos = y(d.position) || 0;
        const desiredWidth = 14;
        const scale = desiredWidth / iconWidth;
        const gap = 4; // space between point and crown bottom
        const tx = xPos - (iconWidth * scale) / 2;
        const ty = yPos - gap - (iconHeight * scale);
        return `translate(${tx}, ${ty}) scale(${scale})`;
      });

    return () => {
      container.selectAll("*").remove();
    };
  }, []);

  return (
    <div>
      <div className="container">
        <h1>Liverpool finishes in the Premier League era</h1>
        <div ref={chartRef} />
      </div>
    </div>
  );
};

export default SimpleVisualizations;